import { GlossaryEntry } from './mockData';

// ── OpenRouter configuration ──────────────────────────────────────────────

const OPENROUTER_URL = 'https://openrouter.ai/api/v1/chat/completions';
export interface ModelConfig {
  id: string;
  label: string;
  draftId: 'A' | 'B' | 'C';
}

/** OpenRouter slugs that 404 or have no endpoints — map to routable replacements. */
const OPENROUTER_MODEL_ALIASES: Record<string, string> = {
  'anthropic/claude-sonnet-4': 'anthropic/claude-sonnet-4.6',
  'anthropic/claude-opus-4': 'anthropic/claude-opus-4.8',
  'anthropic/claude-opus-4.6': 'anthropic/claude-sonnet-4.6',
  'anthropic/claude-opus-4.7': 'anthropic/claude-opus-4.8',
  'anthropic/claude-3.5-sonnet': 'anthropic/claude-sonnet-4.6',
  'google/gemini-2.5-flash-preview': 'google/gemini-2.5-flash',
  'google/gemini-2.5-pro-preview': 'google/gemini-2.5-pro'
};
export function resolveOpenRouterModelId(modelId: string): string {
  let current = modelId.trim();
  const seen = new Set<string>();
  while (OPENROUTER_MODEL_ALIASES[current] && !seen.has(current)) {
    seen.add(current);
    current = OPENROUTER_MODEL_ALIASES[current];
  }
  return current;
}
export function normalizeModelConfigs(configs: ModelConfig[]): ModelConfig[] {
  return configs.map(config => {
    const resolvedId = resolveOpenRouterModelId(config.id);
    return resolvedId === config.id ? config : {
      ...config,
      id: resolvedId
    };
  });
}
export function modelConfigsNeedNormalization(configs: ModelConfig[]): boolean {
  return configs.some(config => resolveOpenRouterModelId(config.id) !== config.id);
}
export const DEFAULT_MODELS: ModelConfig[] = [{
  id: 'anthropic/claude-sonnet-4.6',
  label: 'Claude',
  draftId: 'A'
}, {
  id: 'openai/gpt-4o',
  label: 'GPT-4o',
  draftId: 'B'
}, {
  id: 'google/gemini-2.5-flash',
  label: 'Gemini',
  draftId: 'C'
}];

// ── System message (LawVo Legal Framework v1.0) ──────────────────────────

const SYSTEM_MESSAGE = `You operate under the **LawVo Legal Framework — Translation Protocol (v1.0)**.

### WHO
You are a bilingual legal expert specializing in **Canadian law**, legal drafting, and **professional legal translation**.

### FUNCTION
You translate legal documents and templates while **preserving legal meaning, enforceability, and jurisdictional accuracy**.

### SCOPE
This protocol applies to all **legal documents, policies, agreements, and templates** requiring translation **across Canadian jurisdictions**, unless the user message narrows the document type.

### CORE PRINCIPLES
a. Preserve **legal meaning, intent, and enforceability** at all times.
b. Do **not** perform literal translations where doing so would **distort legal effect**.
c. Maintain **original structure, numbering, and formatting** unless adjustment is **required** for legal accuracy or target-language readability at a professional standard.
d. **Adapt terminology** appropriately between legal systems where **direct equivalence** does not exist.
e. Ensure **clarity, precision**, and drafting consistent with **professional legal standards**.

### JURISDICTION
a. Apply the **jurisdiction** specified in the user message.
b. If jurisdiction is **not** specified, infer from the user's **document context** and apply the **most appropriate** Canadian legal framework.
c. Where required, apply the **legally safest** interpretation among reasonable alternatives—without narrowing obligations or expanding rights beyond what the source text reasonably bears.

### QUEBEC (CIVIL LAW)
When Quebec law applies or is specified:
a. Apply the **Civil Code of Quebec** and **CNESST / LNT** framework **where relevant** to the subject matter.
b. Do **not** introduce or rely on **common law** concepts inappropriate in Quebec civil law.
c. **Replace** incompatible concepts with **appropriate civil law equivalents**.
d. Use **Quebec legal terminology** and **drafting conventions**.
e. **Avoid anglicisms** and **non-Canadian French** phrasing; prefer standard **Canadian French legal** usage.

### CANADA-WIDE / COMMON LAW
When common law or pan-Canadian neutrality is required:
a. Use **neutral** terminology suitable **across Canadian jurisdictions** where possible.
b. Ensure **compatibility** across common law provinces and Quebec **where possible** without distorting the specified governing law.
c. Avoid concepts that create **cross-jurisdictional inconsistency** unless the source text **explicitly** requires them.

### TERMINOLOGY (GLOSSARY + CONSISTENCY)
a. **Glossary supremacy:** When the user message includes a **Glossary**, treat it as **binding** for the cues provided: use the **mandated French** rendering wherever the English uses that cue or a clear morphological variant, subject only to **grammar** (gender, number, syntax). If two entries could apply, prefer the **more specific** cue and remain **consistent** for the remainder of the provided text.
b. Maintain **consistency** in the translation of **key legal terms** throughout the text supplied in the user message.
c. Do **not** vary terminology **unnecessarily**.
d. Prefer **standard Canadian legal terminology** over literal translation where the glossary is silent.

### TONE AND STYLE
a. **Professional, formal, and authoritative.**
b. **Clear and concise**, without unnecessary complexity.
c. Suitable for **enterprise, regulatory, and client-facing** documents.
d. Equivalent to drafting by a **native Canadian legal professional** in the target language.

### OUTPUT (DEFAULT)
a. Output **only** the translated text—**no** commentary, explanations, summaries, or alternative drafts.
b. **Preserve structure, headings, numbering, and formatting** to the extent feasible in plain text.
c. Do **not** prepend labels such as "Translation:".

### QUALITY CONTROL (INTERNAL)
Before returning the final answer, internally verify that:
a. **Legal meaning** has been preserved.
b. **Jurisdictional** accuracy is respected.
c. **Terminology** is consistent and **glossary-compliant**.
d. The text is **fluent** and meets **professional drafting** quality.
e. Any detected internal inconsistency is **corrected** in the final output without narrating the correction process.

### CLARIFICATION
a. If **essential information** is missing to choose governing law, **glossary application**, or **document type** in a way that materially changes legal effect, ask **one concise clarification question** and **stop**.
b. Otherwise, proceed using the **most reasonable legal assumption** consistent with the user message and **do not** add commentary.`;

// ── User message builder ─────────────────────────────────────────────────

interface TranslationRequest {
  sourceText: string;
  segmentId: string;
  glossary: GlossaryEntry[];
  jurisdiction?: string;
  documentContext?: string;
  priorApprovedText?: string;
}
function buildGlossaryBody(glossary: GlossaryEntry[]): string {
  if (glossary.length === 0) return '(No glossary entries provided)';
  return glossary.map(entry => {
    let line = `${entry.english} → ${entry.french}`;
    if (entry.avoid) {
      line += ` (NEVER → ${entry.avoid})`;
    }
    return line;
  }).join('\n');
}
function buildUserMessage(req: TranslationRequest): string {
  const glossaryVersion = `glossary-${new Date().toISOString().slice(0, 10)}`;
  return `## Translation request
**Direction:** English → French
**Jurisdiction / governing law:** ${req.jurisdiction || 'Pan-Canadian neutral (Quebec compatible)'}
**Document type / context (short):** ${req.documentContext || 'Legal document / agreement'}
**Output mode:** target_only

**Glossary (binding)** — version: ${glossaryVersion}
${buildGlossaryBody(req.glossary)}

**Previously approved translation for this segment (optional):**
${req.priorApprovedText || ''}
---

**Source text to translate**

${req.sourceText}

---

**Segment metadata (optional, for traceability only — do not echo in output)**
Segment / clause id: ${req.segmentId}`;
}

// ── OpenRouter API call ──────────────────────────────────────────────────

interface TranslationResult {
  draftId: 'A' | 'B' | 'C';
  modelLabel: string;
  modelId: string;
  text: string;
  error?: string;
}
async function callModel(apiKey: string, modelConfig: ModelConfig, userMessage: string): Promise<TranslationResult> {
  const modelId = resolveOpenRouterModelId(modelConfig.id);
  const requestBody = {
    model: modelId,
    messages: [{
      role: 'system',
      content: SYSTEM_MESSAGE
    }, {
      role: 'user',
      content: userMessage
    }],
    temperature: 0.3,
    // French legal text typically runs 15–25% longer than the English source.
    // The previous 4096-token ceiling silently truncated long paragraphs at
    // the model boundary, producing French that was "cut off at the end". We
    // also check `finish_reason` below so a truncated response is surfaced
    // as an explicit error instead of being approved as-is.
    max_tokens: 16384
  };

  // DEBUG: log everything being sent to the model. Grouped so the dev
  // console stays tidy. The API key is NEVER logged.
  const logLabel = `[Translation] → ${modelConfig.label} (${modelId}) — draft ${modelConfig.draftId}`;
  console.groupCollapsed(logLabel);
  console.log('Endpoint:', OPENROUTER_URL);
  console.log('Request body:', requestBody);
  console.log('SYSTEM PROMPT:\n', SYSTEM_MESSAGE);
  console.log('USER PROMPT:\n', userMessage);
  console.groupEnd();
  const startedAt = performance.now();
  try {
    const response = await fetch(OPENROUTER_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'HTTP-Referer': window.location.origin,
        'X-Title': 'LawVo Translation Workspace'
      },
      body: JSON.stringify(requestBody)
    });
    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`${response.status}: ${errorBody.slice(0, 200)}`);
    }
    const data = await response.json();
    const choice = data.choices?.[0];
    const text = choice?.message?.content?.trim() || '';
    const finishReason: string | undefined = choice?.finish_reason;
    const elapsedMs = Math.round(performance.now() - startedAt);

    // DEBUG: log the response (incl. finish_reason so truncations are visible)
    console.groupCollapsed(`${logLabel} ← response (${elapsedMs}ms, finish=${finishReason ?? 'n/a'})`);
    console.log('Raw response:', data);
    console.log('Extracted text:\n', text);
    console.groupEnd();
    if (!text) {
      throw new Error('Empty response from model');
    }

    // OpenRouter / OpenAI return `finish_reason: 'length'` when the response
    // was cut off because it hit max_tokens. Don't silently accept this — the
    // French would be missing the tail of the segment. Surface it as an error
    // so the user can re-translate (Re-translate button on the workspace).
    if (finishReason === 'length') {
      throw new Error('Response was cut off (hit max_tokens). Re-translate this segment to try again, or break it into smaller paragraphs.');
    }
    return {
      draftId: modelConfig.draftId,
      modelLabel: modelConfig.label,
      modelId,
      text
    };
  } catch (err) {
    const elapsedMs = Math.round(performance.now() - startedAt);
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.warn(`${logLabel} ← error (${elapsedMs}ms): ${message}`);
    return {
      draftId: modelConfig.draftId,
      modelLabel: modelConfig.label,
      modelId,
      text: '',
      error: message
    };
  }
}

// ── Public API ───────────────────────────────────────────────────────────

export interface TranslateSegmentOptions {
  apiKey: string;
  sourceText: string;
  segmentId: string;
  glossary: GlossaryEntry[];
  jurisdiction?: string;
  documentContext?: string;
  priorApprovedText?: string;
  models?: ModelConfig[];
  onDraftComplete?: (result: TranslationResult) => void;
}

/**
 * Translate a segment using 3 models in parallel via OpenRouter.
 * Calls `onDraftComplete` as each model finishes (for progressive UI).
 * Returns all 3 results when done.
 */
export async function translateSegment(options: TranslateSegmentOptions): Promise<TranslationResult[]> {
  const models = options.models || DEFAULT_MODELS;
  const userMessage = buildUserMessage({
    sourceText: options.sourceText,
    segmentId: options.segmentId,
    glossary: options.glossary,
    jurisdiction: options.jurisdiction,
    documentContext: options.documentContext,
    priorApprovedText: options.priorApprovedText
  });

  // Fire all 3 in parallel, report each as it completes
  const promises = models.map(async model => {
    const result = await callModel(options.apiKey, model, userMessage);
    options.onDraftComplete?.(result);
    return result;
  });
  return Promise.all(promises);
}

// ── API key session mirror (canonical storage is Strapi workspace settings) ──

let sessionApiKey = "";
export function getStoredApiKey(): string {
  return sessionApiKey;
}
export function setStoredApiKey(key: string): void {
  sessionApiKey = key;
}